// SK Queue — strictly MONOCHROME palette (black / white / grey only).
//
// The design language communicates state through fills, dots, dashes,
// strike-through and labels — never hue. So unlike the catalog app (which is
// blue-accented), every "accent/info/success/warn/danger/neutral" token here
// collapses onto the ink/grey ramp. This keeps the shared component library
// (Button, Switch, Modal, Chip, …) rendering monochrome automatically, since
// those components read these exact token names from the theme.
//
// Token NAMES are kept identical to the catalog ColorScheme so the reused
// primitives stay type-compatible; only the VALUES change.

export const LightColors = {
  // Surfaces — warm paper system from the handoff
  paper:   '#FAF9F5', // app background (warm off-white)
  paper2:  '#F2F1EC', // subtle fills, hover, foot bars
  paper3:  '#ECECEA', // avatar bg, inactive fills
  card:    '#FFFFFF', // cards, inputs

  // Ink (text)
  ink:     '#08090A', // primary text / primary buttons / occupied tables
  ink2:    '#3D3D40', // secondary text
  ink3:    '#6C6C70', // tertiary text / captions
  ink4:    '#9B9B9F', // placeholder / faint

  // Lines
  line:        '#E4E4E2', // borders, dividers
  lineStrong:  '#CCCCCB', // input borders, stronger dividers

  // Accent → INK. No hue. The "primary" action is solid black.
  accent:      '#08090A',
  accentInk:   '#08090A',
  accentSoft:  '#F2F1EC',
  accentLine:  '#CCCCCB',

  // Info → grey
  info:      '#3D3D40',
  infoSoft:  '#F2F1EC',
  infoLine:  '#CCCCCB',

  // Success → ink (state shown via fill/label, not green)
  success:      '#08090A',
  successSoft:  '#F2F1EC',
  successLine:  '#CCCCCB',

  // Warn → grey
  warn:      '#3D3D40',
  warnSoft:  '#F2F1EC',
  warnLine:  '#CCCCCB',

  // Danger → ink (destructive shown via label/dashed, not red)
  danger:      '#08090A',
  dangerSoft:  '#F2F1EC',
  dangerLine:  '#CCCCCB',

  // Neutral → grey
  neutral:      '#6C6C70',
  neutralSoft:  '#F2F1EC',
  neutralLine:  '#CCCCCB',

  // Semantic aliases
  white: '#FFFFFF',
  black: '#08090A',
  transparent: 'transparent',
};

// Dark scheme = the "board" surfaces from the handoff (now-serving / header
// chrome). Kept monochrome: near-black surfaces, white ink, grey ramp.
export const DarkColors = {
  paper:   '#0B0C0D',
  paper2:  '#141618',
  paper3:  '#1C1D1F',
  card:    '#141618',

  ink:     '#FFFFFF',
  ink2:    '#C9C9C6',
  ink3:    '#9B9B9F',
  ink4:    '#6C6C70',

  line:        '#26282B',
  lineStrong:  '#2A2B2D',

  accent:      '#FFFFFF',
  accentInk:   '#FFFFFF',
  accentSoft:  '#1C1D1F',
  accentLine:  '#2A2B2D',

  info:      '#C9C9C6',
  infoSoft:  '#1C1D1F',
  infoLine:  '#2A2B2D',

  success:      '#FFFFFF',
  successSoft:  '#1C1D1F',
  successLine:  '#2A2B2D',

  warn:      '#C9C9C6',
  warnSoft:  '#1C1D1F',
  warnLine:  '#2A2B2D',

  danger:      '#FFFFFF',
  dangerSoft:  '#1C1D1F',
  dangerLine:  '#2A2B2D',

  neutral:      '#9B9B9F',
  neutralSoft:  '#1C1D1F',
  neutralLine:  '#2A2B2D',

  white: '#FFFFFF',
  black: '#08090A',
  transparent: 'transparent',
};

export type ColorScheme = typeof LightColors;
